export enum VehicleInspectionContextKey {
  Initial = 'initial',
  FarFromDue = 'farFromDue',
  DueSoon = 'dueSoon',
  Overdue = 'overdue',
  NotApplicable = 'notApplicable',
}

export const asVehicleInspectionContextKey = (contextKey?: string): VehicleInspectionContextKey | undefined => {
  if (!contextKey) {
    return undefined;
  }

  return (Object.values(VehicleInspectionContextKey) as string[]).includes(contextKey)
    ? (contextKey as VehicleInspectionContextKey)
    : undefined;
};

export const createDate = (date?: string) => {
  let inspectionDay = '00';
  let inspectionMonth = '00';
  let inspectionYear = '0000';
  if (date) {
    inspectionDay = date.slice(8, 10);
    inspectionMonth = date.slice(5, 7);
    inspectionYear = date.slice(0, 4);
  }
  return `${inspectionDay}.${inspectionMonth}.${inspectionYear}`;
};

const addMonths = (date: Date, months: number) => {
  const dateWithAddedMonths = new Date(date);
  dateWithAddedMonths.setMonth(dateWithAddedMonths.getMonth() + months);
  return dateWithAddedMonths;
};

export const getDaysUntilDate = (targetDate: Date): number => {
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const calculateVehicleAge = (registeredDate?: string): number | undefined => {
  if (!registeredDate) {
    return undefined;
  }

  const regDate = new Date(registeredDate);
  if (Number.isNaN(regDate.getTime())) {
    return undefined;
  }

  const now = new Date();
  const ageInYears = now.getFullYear() - regDate.getFullYear();
  const monthDiff = now.getMonth() - regDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < regDate.getDate())) {
    return ageInYears - 1;
  }

  return ageInYears;
};

export const isGUTestEligible = (registeredDate?: string): boolean => {
  const age = calculateVehicleAge(registeredDate);
  if (age === undefined) {
    return false;
  }
  return age >= 4 && age <= 7;
};

export type UrgencyLevel = 'none' | 'medium' | 'high' | 'critical';

export const getUrgencyLevel = (nextInspectionDate?: string): UrgencyLevel => {
  if (!nextInspectionDate) {
    return 'none';
  }

  const dueDate = new Date(nextInspectionDate);
  if (Number.isNaN(dueDate.getTime())) {
    return 'none';
  }

  const daysUntil = getDaysUntilDate(dueDate);

  if (daysUntil < 0) {
    return 'critical'; // Expired
  }
  if (daysUntil <= 14) {
    return 'high'; // < 14 days
  }
  if (daysUntil <= 42) {
    // ~6 weeks
    return 'medium'; // 6-2 weeks
  }

  return 'none'; // > 6 weeks
};

export const getVehicleInspectionContextKey = (
  nextInspectionDate?: string,
): VehicleInspectionContextKey | undefined => {
  if (!nextInspectionDate) {
    return undefined;
  }

  const dueDate = new Date(nextInspectionDate);

  if (Number.isNaN(dueDate.getTime())) {
    return undefined;
  }

  const daysUntil = getDaysUntilDate(dueDate);
  if (daysUntil < 0) {
    return VehicleInspectionContextKey.Overdue;
  }

  return dueDate > addMonths(new Date(), 6)
    ? VehicleInspectionContextKey.FarFromDue
    : VehicleInspectionContextKey.DueSoon;
};
